//frontend/dispatcher-client/src/components/personnel/PersonnelWorkScheduleModal.jsx
import React, {
  useMemo,
  useState,
} from "react";

import {
  LuCalendarDays,
  LuClock3,
  LuSave,
  LuTrash2,
  LuTruck,
  LuUser,
} from "react-icons/lu";

import Select from "react-select";

import {
  useTranslation,
} from "react-i18next";

import {
  applyPersonnelWorkDaysBulk,
  createPersonnelWorkDay,
  updatePersonnelWorkDay,
  deletePersonnelWorkDay,
} from "../../api/personnelApi";

import {
  markPersonnelResourceStale,
} from "../../utils/personnelCache";

const DEFAULT_WORKING_WEEKDAYS = [
  1,
  2,
  3,
  4,
  5,
];

const pad2 = (
  value
) =>
  String(
    value
  ).padStart(
    2,
    "0"
  );

const toMonthInput = (
  value
) => {
  const date =
    value instanceof Date
      ? value
      : new Date();

  return `${date.getFullYear()}-${pad2(
    date.getMonth() +
      1
  )}`;
};

const getMonthBounds = (
  monthValue
) => {
  const [
    year,
    month,
  ] = String(
    monthValue ||
      ""
  )
    .split("-")
    .map(Number);

  if (
    !Number.isInteger(
      year
    ) ||
    !Number.isInteger(
      month
    ) ||
    month < 1 ||
    month > 12
  ) {
    return null;
  }

  const lastDay =
    new Date(
      year,
      month,
      0
    ).getDate();

  return {
    startDate:
      `${year}-${pad2(
        month
      )}-01`,

    endDate:
      `${year}-${pad2(
        month
      )}-${pad2(
        lastDay
      )}`,
  };
};

const normalizeTimeInput = (
  value,
  fallback = ""
) => {
  if (!value) {
    return fallback;
  }

  return String(
    value
  ).slice(
    0,
    5
  );
};

const getPersonKey = (
  person
) =>
  `${person.personType}:${person.personId}`;

export default function PersonnelWorkScheduleModal({
  mode = "BULK",
  people = [],
  visibleMonth = new Date(),
  initialPersonKeys = [],
  person = null,
  date = null,
  workDay = null,
  onClose,
  onChanged,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "personnel"
    );

  const locale =
    String(
      i18n.resolvedLanguage ||
        i18n.language ||
        "en"
    );

  const isDayMode =
    mode ===
    "DAY";

  const [
    selectedPersonKeys,
    setSelectedPersonKeys,
  ] =
    useState(
      () =>
        isDayMode &&
        person
          ? [
              getPersonKey(
                person
              ),
            ]
          : initialPersonKeys
    );

  const [
    monthValue,
    setMonthValue,
  ] =
    useState(
      () =>
        toMonthInput(
          visibleMonth
        )
    );

  const [
    workingWeekdays,
    setWorkingWeekdays,
  ] =
    useState(
      DEFAULT_WORKING_WEEKDAYS
    );

  const [
    status,
    setStatus,
  ] =
    useState(
      workDay?.status ||
        "WORKING"
    );

  const [
    hasTimes,
    setHasTimes,
  ] =
    useState(
      () =>
        workDay
          ? Boolean(
              workDay.startTime &&
                workDay.endTime
            )
          : true
    );

  const [
    startTime,
    setStartTime,
  ] =
    useState(
      normalizeTimeInput(
        workDay?.startTime,
        "09:00"
      )
    );

  const [
    endTime,
    setEndTime,
  ] =
    useState(
      normalizeTimeInput(
        workDay?.endTime,
        "18:00"
      )
    );

  const [
    breakMinutes,
    setBreakMinutes,
  ] =
    useState(
      workDay?.breakMinutes !=
        null
        ? String(
            workDay.breakMinutes
          )
        : "60"
    );

  const [
    notes,
    setNotes,
  ] =
    useState(
      workDay?.notes ||
        ""
    );

  const [
    saving,
    setSaving,
  ] =
    useState(
      false
    );

  const [
    deleting,
    setDeleting,
  ] =
    useState(
      false
    );

  const [
    confirmDelete,
    setConfirmDelete,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      ""
    );

  const personOptions =
    useMemo(
      () => {
        const users =
          [];

        const drivers =
          [];

        people.forEach(
          (
            item
          ) => {
            const option = {
              value:
                getPersonKey(
                  item
                ),

              label:
                item.displayName,

              person:
                item,
            };

            if (
              item.personType ===
              "DRIVER"
            ) {
              drivers.push(
                option
              );
            } else {
              users.push(
                option
              );
            }
          }
        );

        const sortByLabel =
          (
            first,
            second
          ) =>
            String(
              first.label ||
                ""
            ).localeCompare(
              String(
                second.label ||
                  ""
              ),
              locale
            );

        users.sort(
          sortByLabel
        );

        drivers.sort(
          sortByLabel
        );

        return [
          {
            label:
              t(
                "calendar.filters.users"
              ),

            options:
              users,
          },
          {
            label:
              t(
                "calendar.filters.drivers"
              ),

            options:
              drivers,
          },
        ].filter(
          (
            group
          ) =>
            group.options.length >
            0
        );
      },
      [
        people,
        locale,
        t,
      ]
    );

  const flatPersonOptions =
    useMemo(
      () =>
        personOptions.flatMap(
          (
            group
          ) =>
            group.options
        ),
      [
        personOptions,
      ]
    );

  const selectedPersonOptions =
    useMemo(
      () =>
        flatPersonOptions.filter(
          (
            option
          ) =>
            selectedPersonKeys.includes(
              option.value
            )
        ),
      [
        flatPersonOptions,
        selectedPersonKeys,
      ]
    );

  const weekdayLabels =
    useMemo(
      () => {
        const monday =
          new Date(
            2026,
            7,
            31
          );

        return Array.from(
          {
            length:
              7,
          },
          (
            _,
            index
          ) => ({
            value:
              index +
              1,

            label:
              new Intl.DateTimeFormat(
                locale,
                {
                  weekday:
                    "short",
                }
              ).format(
                new Date(
                  monday.getTime() +
                    index *
                      24 *
                      60 *
                      60 *
                      1000
                )
              ),
          })
        );
      },
      [
        locale,
      ]
    );

  const formattedDate =
    useMemo(
      () => {
        if (!date) {
          return "";
        }

        const [
          year,
          month,
          day,
        ] = String(
          date
        )
          .split("-")
          .map(Number);

        return new Intl.DateTimeFormat(
          locale,
          {
            weekday:
              "long",

            day:
              "2-digit",

            month:
              "long",

            year:
              "numeric",
          }
        ).format(
          new Date(
            year,
            month -
              1,
            day
          )
        );
      },
      [
        date,
        locale,
      ]
    );

  const busy =
    saving ||
    deleting;

  const buildSchedulePayload =
    (
      targetStatus
    ) => {
      if (
        targetStatus ===
        "OFF"
      ) {
        return {
          status:
            "OFF",

          startTime:
            null,

          endTime:
            null,

          breakMinutes:
            0,

          notes:
            notes.trim() ||
            null,
        };
      }

      const parsedBreak =
        Number(
          breakMinutes ||
            0
        );

      if (
        !Number.isInteger(
          parsedBreak
        ) ||
        parsedBreak <
          0
      ) {
        throw new Error(
          t(
            "calendar.work_days.errors.break_invalid",
            {
              defaultValue:
                "Indique uma pausa válida.",
            }
          )
        );
      }

      if (
        hasTimes &&
        (
          !startTime ||
          !endTime
        )
      ) {
        throw new Error(
          t(
            "calendar.work_days.errors.times_required",
            {
              defaultValue:
                "Indique a hora inicial e final.",
            }
          )
        );
      }

      return {
        status:
          "WORKING",

        startTime:
          hasTimes
            ? startTime
            : null,

        endTime:
          hasTimes
            ? endTime
            : null,

        breakMinutes:
          hasTimes
            ? parsedBreak
            : 0,

        notes:
          notes.trim() ||
          null,
      };
    };

  const handleSave =
    async () => {
      setError(
        ""
      );

      setConfirmDelete(
        false
      );

      try {
        setSaving(
          true
        );

        if (
          isDayMode
        ) {
          if (
            !person ||
            !date
          ) {
            throw new Error(
              t(
                "calendar.work_days.errors.person_required",
                {
                  defaultValue:
                    "Selecione uma pessoa.",
                }
              )
            );
          }

          const payload = {
            personType:
              person.personType,

            personId:
              Number(
                person.personId
              ),

            workDate:
              date,

            ...buildSchedulePayload(
              status
            ),
          };

          if (
            workDay?.id
          ) {
            await updatePersonnelWorkDay(
              workDay.id,
              {
                status:
                  payload.status,

                startTime:
                  payload.startTime,

                endTime:
                  payload.endTime,

                breakMinutes:
                  payload.breakMinutes,

                notes:
                  payload.notes,
              }
            );
          } else {
            await createPersonnelWorkDay(
              payload
            );
          }
        } else {
          if (
            selectedPersonOptions.length ===
            0
          ) {
            throw new Error(
              t(
                "calendar.work_days.errors.people_required",
                {
                  defaultValue:
                    "Selecione pelo menos uma pessoa.",
                }
              )
            );
          }

          const bounds =
            getMonthBounds(
              monthValue
            );

          if (!bounds) {
            throw new Error(
              t(
                "calendar.work_days.errors.month_required",
                {
                  defaultValue:
                    "Selecione um mês válido.",
                }
              )
            );
          }

          const schedule =
            buildSchedulePayload(
              "WORKING"
            );

          await applyPersonnelWorkDaysBulk({
            people:
              selectedPersonOptions.map(
                (
                  option
                ) => ({
                  personType:
                    option.person.personType,

                  personId:
                    Number(
                      option.person.personId
                    ),
                })
              ),

            startDate:
              bounds.startDate,

            endDate:
              bounds.endDate,

            workingWeekdays:
              workingWeekdays
                .slice()
                .sort(
                  (
                    first,
                    second
                  ) =>
                    first -
                    second
                ),

            startTime:
              schedule.startTime,

            endTime:
              schedule.endTime,

            breakMinutes:
              schedule.breakMinutes,

            notes:
              schedule.notes,
          });
        }

        markPersonnelResourceStale(
          "workDays"
        );

        if (
          typeof onChanged ===
          "function"
        ) {
          await onChanged();
        }

        onClose();
      } catch (
        saveError
      ) {
        console.error(
          "[PersonnelWorkScheduleModal] save failed:",
          saveError
        );

        setError(
          saveError?.response?.data?.error ||
            saveError?.message ||
            t(
              "calendar.work_days.errors.save_failed",
              {
                defaultValue:
                  "Não foi possível guardar o planeamento.",
              }
            )
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  const handleDelete =
    async () => {
      if (
        !isDayMode ||
        !workDay?.id
      ) {
        return;
      }

      setError(
        ""
      );

      try {
        setDeleting(
          true
        );

        await deletePersonnelWorkDay(
          workDay.id
        );

        markPersonnelResourceStale(
          "workDays"
        );

        if (
          typeof onChanged ===
          "function"
        ) {
          await onChanged();
        }

        onClose();
      } catch (
        deleteError
      ) {
        console.error(
          "[PersonnelWorkScheduleModal] delete failed:",
          deleteError
        );

        setError(
          deleteError?.response?.data?.error ||
            deleteError?.message ||
            t(
              "calendar.work_days.errors.delete_failed",
              {
                defaultValue:
                  "Não foi possível remover a configuração deste dia.",
              }
            )
        );
      } finally {
        setDeleting(
          false
        );
      }
    };

  return (
    <div
      className="personnel-work-days-overlay"
      onMouseDown={(
        event
      ) => {
        if (
          event.target ===
            event.currentTarget &&
          !busy
        ) {
          onClose();
        }
      }}
    >
      <div
        className="personnel-work-days-modal"
        role="dialog"
        aria-modal="true"
      >
        <div className="personnel-work-days-header">
          <div>
            <span>
              {isDayMode
                ? t(
                    "calendar.work_days.day_kicker",
                    {
                      defaultValue:
                        "Planeamento diário",
                    }
                  )
                : t(
                    "calendar.work_days.bulk_kicker",
                    {
                      defaultValue:
                        "Planeamento mensal",
                    }
                  )}
            </span>

            <h3>
              {isDayMode
                ? t(
                    "calendar.work_days.day_title",
                    {
                      defaultValue:
                        "Editar dia de trabalho",
                    }
                  )
                : t(
                    "calendar.work_days.bulk_title",
                    {
                      defaultValue:
                        "Gerir dias de trabalho",
                    }
                  )}
            </h3>
          </div>

          <button
            type="button"
            onClick={
              onClose
            }
            disabled={
              busy
            }
            aria-label={t(
              "common.close"
            )}
          >
            ×
          </button>
        </div>

        {isDayMode ? (
          <div className="personnel-work-days-day-context">
            <div className="personnel-work-days-person-icon">
              {person?.personType ===
              "DRIVER"
                ? <LuTruck />
                : <LuUser />}
            </div>

            <div>
              <strong>
                {person?.displayName ||
                  "—"}
              </strong>

              <span>
                {formattedDate
                  ? formattedDate
                      .charAt(
                        0
                      )
                      .toUpperCase() +
                    formattedDate.slice(
                      1
                    )
                  : "—"}
              </span>
            </div>
          </div>
        ) : (
          <div className="personnel-work-days-form-section personnel-work-days-people-section">
            <div className="personnel-work-days-section-heading">
              <div>
                <strong>
                  {t(
                    "calendar.work_days.people",
                    {
                      defaultValue:
                        "Pessoas",
                    }
                  )}
                </strong>

                <span>
                  {t(
                    "calendar.work_days.people_help",
                    {
                      defaultValue:
                        "Selecione quem recebe este planeamento mensal.",
                    }
                  )}
                </span>
              </div>

              <div className="personnel-work-days-mini-actions">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedPersonKeys(
                      flatPersonOptions.map(
                        (
                          option
                        ) =>
                          option.value
                      )
                    )
                  }
                  disabled={
                    busy ||
                    flatPersonOptions.length ===
                      0
                  }
                >
                  {t(
                    "calendar.work_days.select_all",
                    {
                      defaultValue:
                        "Selecionar todos",
                    }
                  )}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setSelectedPersonKeys(
                      []
                    )
                  }
                  disabled={
                    busy ||
                    selectedPersonKeys.length ===
                      0
                  }
                >
                  {t(
                    "calendar.work_days.clear",
                    {
                      defaultValue:
                        "Limpar",
                    }
                  )}
                </button>
              </div>
            </div>

            <Select
              className="personnel-work-days-people-select"
              classNamePrefix="personnel-work-days-rs"
              value={
                selectedPersonOptions
              }
              options={
                personOptions
              }
              onChange={(
                value
              ) =>
                setSelectedPersonKeys(
                  Array.isArray(
                    value
                  )
                    ? value.map(
                        (
                          option
                        ) =>
                          option.value
                      )
                    : []
                )
              }
              isMulti
              isSearchable
              isClearable
              closeMenuOnSelect={
                false
              }
              hideSelectedOptions={
                false
              }
              isDisabled={
                busy
              }
              placeholder={t(
                "calendar.work_days.people_placeholder",
                {
                  defaultValue:
                    "Pesquisar utilizadores ou motoristas...",
                }
              )}
              noOptionsMessage={() =>
                t(
                  "calendar.work_days.no_people",
                  {
                    defaultValue:
                      "Nenhuma pessoa encontrada",
                  }
                )
              }
              formatOptionLabel={(
                option,
                {
                  context,
                }
              ) => {
                if (
                  context ===
                  "value"
                ) {
                  return option.label;
                }

                return (
                  <div className="personnel-work-days-person-option">
                    <span>
                      {option.person.personType ===
                      "DRIVER"
                        ? <LuTruck />
                        : <LuUser />}
                    </span>

                    <div>
                      <strong>
                        {option.label}
                      </strong>

                      <small>
                        {option.person.personType ===
                        "DRIVER"
                          ? t(
                              "calendar.driver"
                            )
                          : t(
                              "calendar.user"
                            )}
                      </small>
                    </div>
                  </div>
                );
              }}
              menuPortalTarget={
                document.body
              }
              menuPosition="fixed"
              styles={{
                menuPortal:
                  (
                    base
                  ) => ({
                    ...base,
                    zIndex:
                      9900,
                  }),
              }}
            />

            <small className="personnel-work-days-selection-count">
              {t(
                "calendar.work_days.people_selected",
                {
                  defaultValue:
                    "{{count}} selecionadas",

                  count:
                    selectedPersonOptions.length,
                }
              )}
            </small>
          </div>
        )}

        <div className="personnel-work-days-form-grid">
          {!isDayMode && (
            <label className="personnel-work-days-field personnel-work-days-field--wide">
              <span>
                {t(
                  "calendar.work_days.month",
                  {
                    defaultValue:
                      "Mês",
                  }
                )}
              </span>

              <div className="personnel-work-days-input-icon">
                <LuCalendarDays />

                <input
                  type="month"
                  value={
                    monthValue
                  }
                  onChange={(
                    event
                  ) =>
                    setMonthValue(
                      event.target.value
                    )
                  }
                  disabled={
                    busy
                  }
                />
              </div>
            </label>
          )}

          {isDayMode && (
            <div className="personnel-work-days-field personnel-work-days-field--wide">
              <span>
                {t(
                  "calendar.work_days.status",
                  {
                    defaultValue:
                      "Estado planeado",
                  }
                )}
              </span>

              <div className="personnel-work-days-status-toggle">
                <button
                  type="button"
                  className={
                    status ===
                    "WORKING"
                      ? "is-active is-working"
                      : ""
                  }
                  onClick={() =>
                    setStatus(
                      "WORKING"
                    )
                  }
                  disabled={
                    busy
                  }
                >
                  {t(
                    "calendar.work_days.working",
                    {
                      defaultValue:
                        "Trabalha",
                    }
                  )}
                </button>

                <button
                  type="button"
                  className={
                    status ===
                    "OFF"
                      ? "is-active is-off"
                      : ""
                  }
                  onClick={() =>
                    setStatus(
                      "OFF"
                    )
                  }
                  disabled={
                    busy
                  }
                >
                  {t(
                    "calendar.work_days.off",
                    {
                      defaultValue:
                        "Folga",
                    }
                  )}
                </button>
              </div>
            </div>
          )}

          {!isDayMode && (
            <div className="personnel-work-days-field personnel-work-days-field--wide">
              <span>
                {t(
                  "calendar.work_days.working_weekdays",
                  {
                    defaultValue:
                      "Dias de trabalho",
                  }
                )}
              </span>

              <div className="personnel-work-days-weekdays">
                {weekdayLabels.map(
                  (
                    weekday
                  ) => {
                    const active =
                      workingWeekdays.includes(
                        weekday.value
                      );

                    return (
                      <button
                        type="button"
                        key={
                          weekday.value
                        }
                        className={
                          active
                            ? "is-active"
                            : ""
                        }
                        onClick={() =>
                          setWorkingWeekdays(
                            (
                              current
                            ) =>
                              current.includes(
                                weekday.value
                              )
                                ? current.filter(
                                    (
                                      value
                                    ) =>
                                      value !==
                                      weekday.value
                                  )
                                : [
                                    ...current,
                                    weekday.value,
                                  ]
                          )
                        }
                        disabled={
                          busy
                        }
                      >
                        {weekday.label}
                      </button>
                    );
                  }
                )}
              </div>

              <small>
                {t(
                  "calendar.work_days.weekdays_help",
                  {
                    defaultValue:
                      "Os dias não selecionados serão guardados como folga.",
                  }
                )}
              </small>
            </div>
          )}

          {(
            !isDayMode ||
            status ===
              "WORKING"
          ) && (
            <label className="personnel-work-days-checkbox personnel-work-days-field--wide">
              <input
                type="checkbox"
                checked={
                  hasTimes
                }
                onChange={(
                  event
                ) =>
                  setHasTimes(
                    event.target.checked
                  )
                }
                disabled={
                  busy
                }
              />

              <div>
                <strong>
                  {t(
                    "calendar.work_days.define_times",
                    {
                      defaultValue:
                        "Definir horário",
                    }
                  )}
                </strong>

                <span>
                  {t(
                    "calendar.work_days.define_times_help",
                    {
                      defaultValue:
                        "Pode planear apenas o dia, sem horas, se o horário ainda não estiver definido.",
                    }
                  )}
                </span>
              </div>
            </label>
          )}

          {(
            !isDayMode ||
            status ===
              "WORKING"
          ) &&
            hasTimes && (
            <>
              <label className="personnel-work-days-field">
                <span>
                  {t(
                    "calendar.work_days.start_time",
                    {
                      defaultValue:
                        "Hora inicial",
                    }
                  )}
                </span>

                <div className="personnel-work-days-input-icon">
                  <LuClock3 />

                  <input
                    type="time"
                    value={
                      startTime
                    }
                    onChange={(
                      event
                    ) =>
                      setStartTime(
                        event.target.value
                      )
                    }
                    disabled={
                      busy
                    }
                  />
                </div>
              </label>

              <label className="personnel-work-days-field">
                <span>
                  {t(
                    "calendar.work_days.end_time",
                    {
                      defaultValue:
                        "Hora final",
                    }
                  )}
                </span>

                <div className="personnel-work-days-input-icon">
                  <LuClock3 />

                  <input
                    type="time"
                    value={
                      endTime
                    }
                    onChange={(
                      event
                    ) =>
                      setEndTime(
                        event.target.value
                      )
                    }
                    disabled={
                      busy
                    }
                  />
                </div>
              </label>

            <label className="personnel-work-days-field">
              <span>
                {t(
                "calendar.work_days.break_minutes",
                {
                    defaultValue:
                    "Pausa (minutos)",
                }
                )}
              </span>

              <input
                type="number"
                min="0"
                step="1"
                value={
                breakMinutes
                }
                onChange={(
                event
                ) =>
                setBreakMinutes(
                    event.target.value
                )
                }
                disabled={
                busy
                }
              />
            </label>

            <small className="personnel-work-days-overnight-help">
              {t(
                "calendar.work_days.overnight_help",
                {
                defaultValue:
                    "Se a hora final for anterior à inicial, o turno pode continuar para o dia seguinte.",
                }
              )}
             </small>
            </>
          )}

          <label className="personnel-work-days-field personnel-work-days-field--wide">
            <span>
              {t(
                "calendar.work_days.notes",
                {
                  defaultValue:
                    "Notas",
                }
              )}
            </span>

            <textarea
              rows="3"
              value={
                notes
              }
              onChange={(
                event
              ) =>
                setNotes(
                  event.target.value
                )
              }
              placeholder={t(
                "calendar.work_days.notes_placeholder",
                {
                  defaultValue:
                    "Informação adicional, se aplicável...",
                }
              )}
              disabled={
                busy
              }
            />
          </label>
        </div>

        {!isDayMode && (
          <div className="personnel-work-days-overwrite-warning">
            {t(
              "calendar.work_days.overwrite_warning",
              {
                defaultValue:
                  "Aplicar ao mês substitui o planeamento existente dessas pessoas em todos os dias desse mês.",
              }
            )}
          </div>
        )}

        {error && (
          <div className="personnel-work-days-error">
            {error}
          </div>
        )}

        {confirmDelete ? (
          <div className="personnel-work-days-delete-confirm">
            <p>
              {t(
                "calendar.work_days.confirm_delete",
                {
                  defaultValue:
                    "Remover a configuração deste dia? O dia ficará como não configurado.",
                }
              )}
            </p>

            <div>
              <button
                type="button"
                className="personnel-work-days-secondary-btn"
                onClick={() =>
                  setConfirmDelete(
                    false
                  )
                }
                disabled={
                  busy
                }
              >
                {t(
                  "requests.actions.back",
                  {
                    defaultValue:
                      "Voltar",
                  }
                )}
              </button>

              <button
                type="button"
                className="personnel-work-days-danger-btn"
                onClick={
                  handleDelete
                }
                disabled={
                  busy
                }
              >
                {deleting
                  ? t(
                      "requests.actions.working",
                      {
                        defaultValue:
                          "A processar...",
                      }
                    )
                  : t(
                      "calendar.work_days.remove_configuration",
                      {
                        defaultValue:
                          "Remover configuração",
                      }
                    )}
              </button>
            </div>
          </div>
        ) : (
          <div className="personnel-work-days-actions">
            <div>
              {isDayMode &&
                workDay?.id && (
                <button
                  type="button"
                  className="personnel-work-days-delete-btn"
                  onClick={() =>
                    setConfirmDelete(
                      true
                    )
                  }
                  disabled={
                    busy
                  }
                >
                  <LuTrash2 />

                  {t(
                    "calendar.work_days.unconfigure",
                    {
                      defaultValue:
                        "Remover configuração",
                    }
                  )}
                </button>
              )}
            </div>

            <div>
              <button
                type="button"
                className="personnel-work-days-secondary-btn"
                onClick={
                  onClose
                }
                disabled={
                  busy
                }
              >
                {t(
                  "common.close"
                )}
              </button>

              <button
                type="button"
                className="personnel-work-days-save-btn"
                onClick={
                  handleSave
                }
                disabled={
                  busy
                }
              >
                <LuSave />

                {saving
                  ? t(
                      "requests.actions.working",
                      {
                        defaultValue:
                          "A processar...",
                      }
                    )
                  : isDayMode
                  ? t(
                      "calendar.work_days.save_day",
                      {
                        defaultValue:
                          "Guardar dia",
                      }
                    )
                  : t(
                      "calendar.work_days.apply_month",
                      {
                        defaultValue:
                          "Aplicar ao mês",
                      }
                    )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
